import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { openEnquiry } from "@/lib/api";

export function Logo({ light = false, compact = false }) {
    const h = compact ? "h-11" : light ? "h-14" : "h-14 md:h-[68px]";
    const stage = (
        <span className={`logo-stage logo-wave-settle ${h}`}>
            <img src="/logo-base.png" alt="GhumneChaley.com — Sirf Trip Nahi. Yaadein Banane Chale." className="logo-layer logo-base-layer" />
            <img src="/logo-sun.png" alt="" aria-hidden="true" className="logo-layer logo-sun-layer" style={{ left: "28.46%", top: "17.54%", width: "17.07%" }} />
            <img src="/logo-suitcase.png" alt="" aria-hidden="true" className="logo-layer logo-suitcase-layer" style={{ left: "58.27%", top: "52.63%", width: "36.59%" }} />
            <img src="/logo-plane.png" alt="" aria-hidden="true" className="logo-layer logo-plane-layer" style={{ left: "64.5%", top: "12.28%", width: "18.7%" }} />
        </span>
    );
    return (
        <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="GhumneChaley.com home">
            {light ? <span className="bg-cream rounded-xl px-3 py-1.5 inline-flex">{stage}</span> : stage}
        </Link>
    );
}

const NAV = [
    { label: "Home", to: "/#top" },
    { label: "Packages", to: "/packages" },
    { label: "School Trips", to: "/school-trips" },
    { label: "College Trips", to: "/college-trips" },
    { label: "Corporate", to: "/corporate-trips" },
    { label: "Industrial Visits", to: "/industrial-visits" },
    { label: "Destinations", to: "/destinations" },
    { label: "About Us", to: "/#about" },
];

export function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Hysteresis: shrink past 72px, expand only above 28px — a single
        // threshold makes the bar flicker when scrolling slowly around it.
        const onScroll = () => setScrolled((prev) => (prev ? window.scrollY > 28 : window.scrollY > 72));
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (to) => (e) => {
        setOpen(false);
        if (to.startsWith("/#")) {
            e.preventDefault();
            const id = to.slice(2);
            if (window.location.pathname !== "/") {
                navigate("/");
                setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }
        }
    };

    return (
        <>
            {/* fixed header + static spacer: the height swap can no longer
                reflow the page — this is what caused the scroll glitch */}
            <div className="h-24" aria-hidden="true" />
            <header data-testid="site-header" className="fixed top-0 inset-x-0 z-40 bg-cream/95 backdrop-blur-md border-b border-stone-300/70">
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-[height,box-shadow] duration-300 ${scrolled ? "h-16 shadow-[0_2px_12px_rgba(30,58,95,0.08)]" : "h-24"}`}>
                <Logo compact={scrolled} />
                <nav className="hidden lg:flex items-center gap-6 text-[11px] font-bold uppercase tracking-wider text-stone-600" data-testid="desktop-nav">
                    {NAV.map((n) => (
                        <Link key={n.label} to={n.to} onClick={go(n.to)} className="hover:text-saffron transition-colors whitespace-nowrap">
                            {n.label}
                        </Link>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => openEnquiry()}
                        data-testid="header-plan-my-trip-btn"
                        className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded-full text-xs font-display font-black uppercase tracking-wider text-white bg-saffron hover:bg-saffron-dark transition-colors"
                    >
                        Plan My Trip
                    </button>
                    <button className="lg:hidden p-2 text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden border-t border-stone-200 bg-cream px-4 py-4 space-y-1" data-testid="mobile-menu">
                    {NAV.map((n) => (
                        <Link key={n.label} to={n.to} onClick={go(n.to)} className="block py-2.5 px-2 text-sm font-bold uppercase tracking-wider text-stone-700 hover:text-saffron rounded-lg hover:bg-cream-dark">
                            {n.label}
                        </Link>
                    ))}
                    <button
                        onClick={() => { setOpen(false); openEnquiry(); }}
                        data-testid="mobile-plan-my-trip-btn"
                        className="mt-2 w-full rounded-full bg-saffron text-white font-display font-black text-sm uppercase tracking-wider py-3"
                    >
                        Plan My Trip
                    </button>
                </div>
            )}
            </header>
        </>
    );
}
